'use client'

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { type CartItem, type CategoryDTO, type Currency, type Option } from 'ecosystem'
import { getCartItemPrice, sumCart } from 'shared-utils'
import { createCategoryMap, ga4EcommerceItemFactoryFromCartItems } from '../utils'
import { useGtmEvents } from './useGtmEvents'
import { useGtmPurchaseEvent } from './useGtmPurchaseEvent'

interface UseViewCartProps {
  categories: Option<CategoryDTO[]>
  cartItems: CartItem[]
  cartTotal: number
  currency: Currency
  registerViewCartEvent?: boolean
}

export const useGtmCartEvents = ({
  categories,
  cartTotal,
  cartItems,
  currency,
  registerViewCartEvent
}: UseViewCartProps) => {
  const {
    trackAddToCartEvent,
    trackRemoveFromCartEvent,
    trackBeginCheckoutEvent,
    trackViewCartEvent
  } = useGtmEvents()

  const categoryMap = useMemo(() => createCategoryMap(categories), [categories])

  const isViewCartEventTracked = useRef(false)

  const viewCartEvent = useCallback(
    () =>
      trackViewCartEvent({
        ecommerce: {
          value: sumCart(cartItems),
          currency,
          items: ga4EcommerceItemFactoryFromCartItems(cartItems, categoryMap)
        }
      }),
    [cartItems, categoryMap, currency, trackViewCartEvent]
  )

  useEffect(() => {
    if (!isViewCartEventTracked.current && registerViewCartEvent) {
      isViewCartEventTracked.current = viewCartEvent()
    }
  }, [cartItems, cartTotal, currency, registerViewCartEvent, viewCartEvent])

  const addToCartEvent = useCallback(
    (cartItem: CartItem) => {
      trackAddToCartEvent({
        ecommerce: {
          value: getCartItemPrice(cartItem) * cartItem.qty,
          currency,
          items: ga4EcommerceItemFactoryFromCartItems([cartItem], categoryMap)
        }
      })
    },
    [categoryMap, currency, trackAddToCartEvent]
  )

  const removeFromCartEvent = useCallback(
    (cartItem: CartItem) => {
      trackRemoveFromCartEvent({
        ecommerce: {
          value: getCartItemPrice(cartItem) * cartItem.qty,
          currency,
          items: ga4EcommerceItemFactoryFromCartItems([cartItem], categoryMap)
        }
      })
    },
    [categoryMap, currency, trackRemoveFromCartEvent]
  )

  const checkoutEvent = useCallback(() => {
    trackBeginCheckoutEvent({
      ecommerce: {
        value: cartTotal,
        currency,
        // todo-gtm-events: discount could be added
        // coupon: '',
        items: ga4EcommerceItemFactoryFromCartItems(cartItems, categoryMap)
      }
    })
  }, [cartItems, cartTotal, categoryMap, currency, trackBeginCheckoutEvent])

  const { purchaseEvent } = useGtmPurchaseEvent({ categories, currency })

  const handleAddToCartOrQuantityChange = useCallback(
    (newCartItem: CartItem, prevCartItem?: CartItem) => {
      if (!prevCartItem) {
        addToCartEvent(newCartItem)
        return
      }

      const diff = newCartItem.qty - prevCartItem.qty
      const cartItemEventPayload = {
        ...newCartItem,
        qty: Math.abs(diff)
      }

      if (diff > 0) {
        addToCartEvent(cartItemEventPayload)
      } else if (diff < 0) {
        removeFromCartEvent(cartItemEventPayload)
      } else {
        addToCartEvent(newCartItem)
      }
    },
    [addToCartEvent, removeFromCartEvent]
  )

  return {
    checkoutEvent,
    purchaseEvent,
    addToCartEvent,
    removeFromCartEvent,
    viewCartEvent,
    isMounted: isViewCartEventTracked.current,
    handleAddToCartOrQuantityChange
  }
}
